import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { readFilteredEntity, createEntity, updateEntity, deleteEntity } from "../services/crudService";
import { Review } from "../interfaces/entities/review";
import { Collection } from "../enums/collections.enum";
import { Timestamp, WhereFilterOp } from "firebase/firestore";

export interface ReviewFilter {
  companyId?: string;
  branchId?: string;
  userId?: string;
  minRating?: number;
  maxRating?: number;
  fromDate?: string;
  toDate?: string;
}

export type MonthType = 'current' | 'previous';

export interface MonthReviewsFilter {
  branchId: string;
  companyId?: string;
  monthType: MonthType;
}

const createTimestampFromDateString = (dateString: string, isEndOfDay: boolean): Timestamp => {
  const [yearText, monthText, dayText] = dateString.split("-");
  const year = Number(yearText);
  const month = Number(monthText) - 1;
  const day = Number(dayText);

  const date = isEndOfDay
    ? new Date(year, month, day, 23, 59, 59, 999)
    : new Date(year, month, day, 0, 0, 0, 0);

  return Timestamp.fromDate(date);
};

export const reviewApi = createApi({
  reducerPath: "reviewApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [Collection.Reviews],
  endpoints: (build) => ({
    getReviews: build.query<Review[], ReviewFilter>({
      async queryFn(filtersInput) {
        try {
          const filters = [];

          if (filtersInput.companyId)
            filters.push({ field: "companyId", operator: "==" as WhereFilterOp, value: filtersInput.companyId });

          if (filtersInput.branchId)
            filters.push({ field: "branchId", operator: "==" as WhereFilterOp, value: filtersInput.branchId });

          if (filtersInput.userId)
            filters.push({ field: "userId", operator: "==" as WhereFilterOp, value: filtersInput.userId });

          if (filtersInput.minRating !== undefined)
            filters.push({ field: "rating", operator: ">=" as WhereFilterOp, value: filtersInput.minRating });

          if (filtersInput.maxRating !== undefined)
            filters.push({ field: "rating", operator: "<=" as WhereFilterOp, value: filtersInput.maxRating });

          if (filtersInput.fromDate)
            filters.push({
              field: "createdAt",
              operator: ">=" as WhereFilterOp,
              value: createTimestampFromDateString(filtersInput.fromDate, false),
            });

          if (filtersInput.toDate)
            filters.push({
              field: "createdAt",
              operator: "<=" as WhereFilterOp,
              value: createTimestampFromDateString(filtersInput.toDate, true),
            });

          const data = await readFilteredEntity<Review>(Collection.Reviews, filters);
          return { data: data || [] };
        } catch (error: any) {
          return { error: { status: 500, message: error.message || "Failed to fetch reviews" } };
        }
      },
      providesTags: (result) =>
        result ? result.map((r) => ({ type: Collection.Reviews, id: r.id })) : [],
    }),
    
    checkExistingReview: build.query<boolean, { branchId: string; browserId: string; date: string }>({
      async queryFn({ branchId, browserId, date }) {
        try {
          const filters = [
            { field: "branchId", operator: "==" as WhereFilterOp, value: branchId },
            { field: "browserId", operator: "==" as WhereFilterOp, value: browserId },
            { field: "date", operator: "==" as WhereFilterOp, value: date },
          ];

          const data = await readFilteredEntity<Review>(Collection.Reviews, filters);
          return { data: (data && data.length > 0) || false };
        } catch (error: any) {
          return { error: { status: 500, message: error.message || "Failed to check existing review" } };
        }
      },
    }),
  
    upsertReview: build.mutation<Review, Review>({
      async queryFn(reviewData) {
        try {
          // Check if review already exists for this browser/user combination
          const existingFilters = [
            { field: "companyId", operator: "==" as WhereFilterOp, value: reviewData.companyId },
            { field: "branchId", operator: "==" as WhereFilterOp, value: reviewData.branchId },
            { field: "userId", operator: "==" as WhereFilterOp, value: reviewData.userId },
            { field: "reviewerId", operator: "==" as WhereFilterOp, value: reviewData.reviewerId },
          ];

          const existingReviews = await readFilteredEntity<Review>(Collection.Reviews, existingFilters);
          
          if (existingReviews && existingReviews.length > 0) {
            // Update existing review
            const existingReview = existingReviews[0];
            const updatedReviewData: Review = {
              ...existingReview,
              rating: reviewData.rating,
              comment: reviewData.comment,
              reviewerPhone: reviewData.reviewerPhone,
              editCount: (existingReview.editCount || 0) + 1,
              updatedAt: Timestamp.fromDate(new Date()),
            };
            
            await updateEntity(Collection.Reviews, existingReview.id!, updatedReviewData);
            return { data: { ...updatedReviewData, id: existingReview.id } as Review };
          }

          // Create new review
          const result = await createEntity(Collection.Reviews, reviewData);
          return { data: result as Review };
        } catch (error: any) {
          return { error: { status: 500, message: error.message || "Failed to upsert review" } };
        }
      },
      invalidatesTags: [{ type: Collection.Reviews }],
    }),

    getMonthReviews: build.query<Review[], MonthReviewsFilter>({
      async queryFn({ branchId, companyId, monthType }) {
        try {
          // Calculate date range based on month type
          const now = new Date();
          let firstDayOfMonth: Date;
          let lastDayOfMonth: Date;

          if (monthType === 'previous') {
            firstDayOfMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            lastDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
          } else {
            // current month
            firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
          }

          const filters = [
            { field: "branchId", operator: "==" as WhereFilterOp, value: branchId },
            { field: "createdAt", operator: ">=" as WhereFilterOp, value: Timestamp.fromDate(firstDayOfMonth) },
            { field: "createdAt", operator: "<=" as WhereFilterOp, value: Timestamp.fromDate(lastDayOfMonth) },
          ];

          // if (companyId) {
          //   filters.push({ field: "companyId", operator: "==" as WhereFilterOp, value: companyId });
          // }

          const data = await readFilteredEntity<Review>(Collection.Reviews, filters);
          return { data: data || [] };
        } catch (error: any) {
          return { error: { status: 500, message: error.message || "Failed to fetch month reviews" } };
        }
      },
      providesTags: (result) =>
        result ? result.map((r) => ({ type: Collection.Reviews, id: r.id })) : [],
    }),

    deleteReview: build.mutation<void, string>({
      async queryFn(reviewId) {
        try {
          await deleteEntity(Collection.Reviews, reviewId);
          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 500, message: error.message || "Failed to delete review" } };
        }
      },
      invalidatesTags: [{ type: Collection.Reviews }],
    }),
  }),
});

export const {
  useGetReviewsQuery,
  useCheckExistingReviewQuery,
  useUpsertReviewMutation,
  useGetMonthReviewsQuery,
  useDeleteReviewMutation,
} = reviewApi;

